/**
 * H15 — Menu drawer navigation stack
 * Figma `50:10284` "Shared behavior": chevron → push · Back → pop one ·
 * gender change → reset to root · arbitrary depth.
 */
import { computed, ref, type Ref } from 'vue'
import type { FairHomeV2MenuNode } from './fair-home-v2-menu-drawer.data'

export function useFairHomeV2MenuStack(roots: Ref<FairHomeV2MenuNode[]>) {
  /** Path of drilled-in nodes, root level = []. */
  const stack = ref<FairHomeV2MenuNode[]>([])

  const depth = computed(() => stack.value.length)
  const isRoot = computed(() => stack.value.length === 0)

  /** Node whose children are currently listed (undefined at root). */
  const current = computed<FairHomeV2MenuNode | undefined>(
    () => stack.value[stack.value.length - 1],
  )

  const rows = computed<FairHomeV2MenuNode[]>(
    () => current.value?.children ?? roots.value,
  )

  const title = computed(() => current.value?.label ?? '')

  function push(node: FairHomeV2MenuNode) {
    stack.value = [...stack.value, node]
  }

  function pop() {
    if (stack.value.length === 0) return
    stack.value = stack.value.slice(0, -1)
  }

  function reset() {
    stack.value = []
  }

  /** Full breadcrumb for a row: ancestors + the row itself. */
  function pathTo(node: FairHomeV2MenuNode): FairHomeV2MenuNode[] {
    return [...stack.value, node]
  }

  return { stack, depth, isRoot, current, rows, title, push, pop, reset, pathTo }
}
